import { AdditiveBlending, Color, ShaderMaterial } from 'three';
import WebGLObject from '../_app/cuchillo/3D/WebGLObject';
import { PLANE_GEOMETRY } from './constants';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { isPortrait } from '../_app/cuchillo/core/Basics';

// Godrays SUBMARINS = CORTINES de llum (light shafts) que baixen des de la superfície. Pla frontal amb
// ALPHA 100% procedural (FBM 1D suau al llarg de X → columnes contínues; `gap` les SEPARA entre si).
// COL·LOCACIÓ: la cortina va CENTRADA sobre el seu .__target (com un Image/WebGLObject normal). GLOW:
// nucli calent (blanc) + guany HDR + wash de base perquè sembli LLUM. Additiu, 2n render (underwater).
// Segueix l'scroll (Y del rect del .__target cada frame).
export const GODRAYS_SETTINGS = {
  color: '#bfe0ff',      // color de la llum. String → colorpicker al pane.

  // ── FORMA (compartida per tots els rajos) ──
  gap: 0.48,             // SEPARA/ajunta les columnes (llindar): ↑ = més separades/primes/transparents
  persist: 0.95,          // BASE estàtica: sempre queden alguns rajos (mai desapareixen tots). 0 = només animat
  contrast: 1.8,         // nitidesa dels rajos (pow sobre el perfil FBM)
  sway: 0.025,           // amplitud del balanceig horitzontal (refracció de la superfície)
  swayFreq: 0.8,         // freqüència del balanceig al llarg del raig
  converge: 1,           // convergència cap a dalt (1 = rectes; ↑ = més estrets a la superfície)
  // (fades dalt/baix/vores i l'ondulació stretchY estan CONGELATS com a constants al shader.)

  // ── GLOW (perquè sembli llum, no una taca) ──
  glow: 1.65,            // guany de brillantor (>1 → nuclis clipen cap a blanc, HDR)
  hot: 2,                // quant els nuclis dels rajos tendeixen a BLANC (font de llum)

  // ── LOOK (valors únics) ──
  intensity: 0.25,       // brillantor global
  speed: 0.53,           // velocitat d'animació
  noiseScale: 5.8,       // nº de columnes

  // ── COL·LOCACIÓ (ancorada al target; valors ESTÀTICS, iguals per a totes les instàncies). Els
  //    data-attributes del .__target (data-offset-x/y, data-rotation) els sobreescriuen per instància. ──
  rotate: 40,            // angle dels rajos (graus)
  offsetX: -0.61,        // desplaçament X (× amplada del target)
  offsetY: 0,            // desplaçament Y (× alçada del target)
  scale: 1.25,           // mida (× mida del target)
  scrollSpeed: .1,      // PARALLAX d'scroll: 0 = totalment estàtic · 1 = segueix l'scroll 1:1 (com abans).
  //                        Petit = deriva molt lenta en la direcció de l'scroll. Override: data-scroll-speed.
  scrollRotate: .8,     // ROTACIÓ subtil lligada a l'scroll: graus per pantalla de "delta" (respecte l'àncora).
  //                        A repòs (delta 0) queda a la rotació base. Override: data-scroll-rotate.
  anchorY: 0.5,          // punt "central" de repòs (fracció d'alçada de pantalla): el godray queda a "casa"
  anchorYPortrait: 0.7,  //   quan el seu target és en aquesta fracció. En portrait el baixem a 0.7.
};

// SEED per instància: cada godray desplaça el domini del noise (columnes) i la fase del sway → patró
// diferent. Determinista (comptador d'instàncies × spread), o override amb data-seed al .__godray-target.
let _seedCounter = 0;
const SEED_SPREAD = 37.13; // separació entre seeds (gran/irregular = patrons ben diferents)

const VERT = /* glsl */`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;

const FRAG = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform float uTime, uIntensity, uNoiseScale, uContrast, uSpeed, uGap;
  uniform float uSway, uSwayFreq, uConverge, uPersist, uSeed;
  uniform float uGlow, uHot;
  uniform vec3 uColor;

  float _hash(float n) { return fract(sin(n) * 43758.5453123); }
  // Value noise 1D (suau, interpolat) → base contínua per a les columnes (no cel·lular).
  float _noise1(float x) {
    float i = floor(x), f = fract(x);
    float u = f * f * (3.0 - 2.0 * f);
    return mix(_hash(i), _hash(i + 1.0), u);
  }
  // FBM 1D (4 octaves) → perfil de columnes suau amb detall.
  float _fbm1(float x) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 4; i++) { v += a * _noise1(x); x *= 2.0; a *= 0.5; }
    return v;
  }

  void main() {
    vec2 uv = vUv;

    // Convergència: rajos més estrets a DALT (superfície) i més oberts a BAIX.
    float x = (uv.x - 0.5) / mix(1.0, uConverge, uv.y) + 0.5;
    // Balanceig horitzontal (refracció de la superfície ondulada) al llarg del raig. uSeed desfasa el sway.
    x += sin(uv.y * uSwayFreq + uTime * uSpeed + uSeed) * uSway;

    // Perfil de les columnes: FBM SUAU al llarg de X, 2 capes a velocitats diferents → shimmer viu.
    // uSeed desplaça el domini del noise → cada instància, columnes diferents.
    float ray = x * uNoiseScale + uSeed;
    float s1 = _fbm1(ray + uTime * uSpeed * 0.25);
    float s2 = _fbm1(ray * 1.9 + 5.0 - uTime * uSpeed * 0.35);
    float animated = clamp(s1 * 0.6 + s2 * 0.4, 0.0, 1.0);
    // BASE estatica (NO segueix el temps): garanteix que SEMPRE quedin alguns rajos, encara que
    // l'animacio caigui tota per sota del threshold. persist = quant de terra aporta (0 = nomes animat).
    // Segueix el sway (esta en l'espai ray), aixi que no queda congelada. max() = terra dur -> mai buit.
    float base = _fbm1(ray + 17.0);
    float raw = max(animated, base * uPersist);
    // gap: SEPARA les columnes (↑ = més separades/primes/transparents; només sobreviuen els pics).
    float shafts = pow(smoothstep(uGap, 1.0, raw), uContrast);

    // Fades CONGELATS (constants): dalt/baix (superfície→profunditat) + vores esquerra/dreta.
    float vfall = smoothstep(0.0, 0.35, uv.y) * smoothstep(1.0, 0.78, uv.y);  // smoothBottom .35, smoothTop .22
    float hfall = smoothstep(0.0, 0.13, uv.x) * smoothstep(1.0, 0.87, uv.x);  // edgeSoftness .13
    float mask = vfall * hfall;

    float a = clamp(shafts * mask * uIntensity, 0.0, 1.0);
    // Nucli CALENT: els centres brillants tendeixen a BLANC (font de llum) + guany HDR → glow.
    vec3 col = mix(uColor, vec3(1.0), clamp(shafts * uHot, 0.0, 1.0)) * uGlow;
    if (a <= 0.001) discard;
    gl_FragColor = vec4(col, a);               // AdditiveBlending → dst + col*a
  }
`;

export default class Godrays extends WebGLObject {
  constructor(dom) {
    super({ dom, geometry: PLANE_GEOMETRY, hasMouse: false });
    // Overrides per-instància des de data-attributes del .__target: si existeixen, SOBREESCRIUEN el
    // setting estàtic per aquesta instància (si no, tots els godrays comparteixen el mateix setting).
    // offsetX/Y = fracció (× amplada/alçada del target, com els settings); rotation = graus.
    // data-offset-x, data-offset-y, data-rotation.
    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
    const ds = dom ? dom.dataset : {};
    this._ovOffX = num(ds.offsetX);
    this._ovOffY = num(ds.offsetY);
    this._ovRotate = num(ds.rotation);
    this._ovScrollSpeed = num(ds.scrollSpeed);
    this._ovScrollRot = num(ds.scrollRotate);
    this._alignRight = ds.align === 'right'; // right → l'offset X va cap a l'altre costat (signe contrari)
    // Seed únic per instància (override data-seed; si no, comptador determinista) → patró propi.
    this._seed = num(ds.seed) != null ? num(ds.seed) : (_seedCounter++ * SEED_SPREAD);
    this._setupMaterial();
    this.init();
    this.resize();
  }

  _setupMaterial() {
    const s = GODRAYS_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(s.color) },
        uIntensity: { value: s.intensity },
        uNoiseScale: { value: s.noiseScale },
        uSpeed: { value: s.speed },
        uGap: { value: s.gap },
        uContrast: { value: s.contrast },
        uSway: { value: s.sway },
        uSwayFreq: { value: s.swayFreq },
        uConverge: { value: s.converge },
        uPersist: { value: s.persist },
        uGlow: { value: s.glow },
        uHot: { value: s.hot },
        uSeed: { value: this._seed },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });
  }

  // Layer del double render (el posa Scene → GODRAY_LAYER, 2n render / underwater).
  setLayer(n) { this.traverse((o) => o.layers.set(n)); }

  // Animació per-frame (des de Scene.loop) + reposició per PARALLAX (calcula la seva pròpia X,Y des de
  // Scroll.y; NO llegeix el DOM — això només passa al resize).
  loop(dt) {
    if (this.material) this.material.uniforms.uTime.value += dt;
    this._reposition();
  }

  // Settings en viu (debug): FORMA/GLOW/LOOK compartits (valors únics).
  setConfig(partial = {}) {
    Object.assign(GODRAYS_SETTINGS, partial);
    if (!this.material) return;
    const u = this.material.uniforms;
    const s = GODRAYS_SETTINGS;
    u.uColor.value.set(s.color);
    u.uIntensity.value = s.intensity;
    u.uNoiseScale.value = s.noiseScale;
    u.uGap.value = s.gap;
    u.uContrast.value = s.contrast;
    u.uSpeed.value = s.speed;
    u.uSway.value = s.sway;
    u.uSwayFreq.value = s.swayFreq;
    u.uConverge.value = s.converge;
    u.uPersist.value = s.persist;
    u.uGlow.value = s.glow;
    u.uHot.value = s.hot;
    this.resize(); // reaplica escala/offset (col·locació) en viu
  }

  // Col·locació per PARALLAX: calcula la seva PRÒPIA X,Y cada frame des de Scroll.y, sobre l'àncora en
  // coords de DOCUMENT cachejada al resize (_homeDocY/_baseCenterX) → NO llegeix el DOM aquí.
  //   delta = distància (px) del centre del target al centre de pantalla → 0 quan el target està centrat
  //   (progress 0.5) → el godray queda a la seva posició "original" (l'offset del seu target).
  //   Amb scrollSpeed petit, deriva molt lenta en la direcció de l'scroll.
  // Anchor de finestra REAL (com la càmera): center = innerWidth/Height / 2 (robust al device inspector).
  _reposition() {
    if (this._homeDocY == null) return;
    const s = GODRAYS_SETTINGS;
    const W = window.innerWidth, H = window.innerHeight;
    let offX = this._ovOffX != null ? this._ovOffX : s.offsetX;
    const offY = this._ovOffY != null ? this._ovOffY : s.offsetY;
    let rot = this._ovRotate != null ? this._ovRotate : s.rotate;
    const spd = this._ovScrollSpeed != null ? this._ovScrollSpeed : s.scrollSpeed;
    const spinScroll = this._ovScrollRot != null ? this._ovScrollRot : s.scrollRotate;
    if (this._alignRight) { offX = -offX; rot = -rot; } // alineat a la dreta → offset X i rotació en mirall
    const anchorY = isPortrait ? s.anchorYPortrait : s.anchorY; // punt "central" de repòs (portrait més avall)
    const delta = (this._homeDocY - (Scroll.y || 0)) - H * anchorY; // 0 = target a anchorY (repòs)
    // Rotació MOLT subtil lligada a l'scroll: graus per pantalla de delta. A delta=0 (repòs) → rotació base.
    const rotScroll = (this._alignRight ? -1 : 1) * (delta / H) * spinScroll;
    this.position.x = (this._baseCenterX - W / 2) + offX * this._sizeW; // estàtic (l'scroll vertical no el mou)
    this.position.y = -delta * spd + offY * this._sizeH;               // deriva LENTA; centrat a delta=0
    this.rotation.z = (rot + rotScroll) * Math.PI / 180;              // base + rotació d'scroll (subtil)
  }

  // Es dimensiona respecte el TARGET × escala i re-ancora la posició "original" (ÚNICA lectura del DOM):
  // cacheja el centre del target en coords de DOCUMENT (fix, independent de l'scroll) + la mida.
  resize() {
    if (!this.dom || !this.mesh) return;
    const s = GODRAYS_SETTINGS;
    const r = this.dom.getBoundingClientRect();
    super.resize(r.width * s.scale, r.height * s.scale);
    this._homeDocY = r.top + r.height / 2 + (Scroll.y || 0); // centre Y del target en coords de document
    this._baseCenterX = r.left + r.width / 2;                // centre X (screen = doc; l'scroll vertical no el mou)
    this._sizeW = r.width;
    this._sizeH = r.height;
    this._reposition();
  }

  dispose() { super.dispose(); }
}
